import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import MDButton from 'src/components/md/MDButton';
import Modal from 'src/components/modal/Modal';
import { Grid } from '@mui/material';
import { FamousSalesOrderDTO } from 'src/api/temp/famous/useFamousSalesOrders';
import useSendEdiTransaction from 'src/api/temp/famous/useSendEdiTransaction';
import React, { useState, useContext } from 'react';
import Icon from '@mui/material/Icon';
import config from 'src/config';
import Alert from '@mui/material/Alert';
import { AuthContext } from 'src/context/auth.context';
import { useViewMode } from 'src/context/view-mode.context';
import { isAdmin } from 'src/utils/user';

interface Props {
  famousSalesOrder: FamousSalesOrderDTO;
  onClose: (refetch?: boolean) => void;
}

// Custom JSON viewer to match the pattern used elsewhere in the codebase
interface JsonObject {
  [key: string]: JsonValue;
}

type JsonValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | JsonObject
  | Array<JsonValue>;

const JsonViewer = ({
  data,
  level = 0,
}: {
  data: JsonValue;
  level?: number;
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    // Expand first level by default
    root: level < 1,
  });

  if (data === null) return <span style={{ color: '#999' }}>null</span>;
  if (data === undefined)
    return <span style={{ color: '#999' }}>undefined</span>;

  if (typeof data !== 'object') {
    // Handle primitive values
    if (typeof data === 'string') {
      return (
        <span
          style={{
            color: '#009688',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
          }}
        >
          &quot;{data}&quot;
        </span>
      );
    }
    if (typeof data === 'number') {
      return <span style={{ color: '#1976d2' }}>{data}</span>;
    }
    if (typeof data === 'boolean') {
      return (
        <span style={{ color: '#ff9800' }}>{data ? 'true' : 'false'}</span>
      );
    }
    return <span style={{ wordBreak: 'break-word' }}>{String(data)}</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <span>[ ]</span>;
    }

    return (
      <div style={{ paddingLeft: level > 0 ? 20 : 0, width: '100%' }}>
        <span
          onClick={() => setExpanded((prev) => ({ ...prev, root: !prev.root }))}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          {expanded.root ? '▼' : '▶'} Array [{data.length}]
        </span>

        {expanded.root && (
          <div style={{ paddingLeft: 20, width: '100%' }}>
            {data.map((item, i) => (
              <div key={i} style={{ width: '100%' }}>
                <span style={{ color: '#555', marginRight: 8 }}>[{i}]:</span>
                <JsonViewer data={item} level={level + 1} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Object
  const keys = Object.keys(data);
  if (keys.length === 0) {
    return <span>{}</span>;
  }

  return (
    <div style={{ paddingLeft: level > 0 ? 20 : 0, width: '100%' }}>
      <span
        onClick={() => setExpanded((prev) => ({ ...prev, root: !prev.root }))}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        {expanded.root ? '▼' : '▶'} Object{' '}
        {`{${keys.length} ${keys.length === 1 ? 'key' : 'keys'}}`}
      </span>

      {expanded.root && (
        <div style={{ paddingLeft: 20, width: '100%' }}>
          {keys.map((key) => (
            <div
              key={key}
              style={{
                wordBreak: 'break-word',
                marginBottom: 2,
                width: '100%',
              }}
            >
              <span
                style={{ color: '#673ab7', fontWeight: 'bold', marginRight: 8 }}
              >
                {key}:
              </span>
              <JsonViewer data={data[key]} level={level + 1} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FamousSalesOrderModal: React.FC<Props> = ({
  famousSalesOrder,
  onClose,
}) => {
  const [showRawData, setShowRawData] = useState(false);
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const isAdminUser = isAdmin(currentUser, viewMode);

  const ediInvoiceType = famousSalesOrder.ediInvoiceType;
  const sendEdiTransaction = useSendEdiTransaction(() => {
    onClose(true);
  });

  const handleSendEdi = (test = false) => {
    sendEdiTransaction.mutate({
      famousSalesOrderId: famousSalesOrder.id,
      test,
    });
  };

  // Extract the first order details for display (assuming most orders have one set of details)
  const orderDetails = famousSalesOrder.orderDetails?.[0];
  const customer = orderDetails?.customer;
  const shipTo = orderDetails?.shipTo?.[0];
  const payTerms = orderDetails?.payterms;
  const carrier = orderDetails?.carrier;

  // Check for translate issues to block EDI sending
  const linesWithProducts =
    orderDetails?.lines?.filter(
      (line) => line.product && line.product.length > 0,
    ) || [];
  const linesWithTranslateIssues = linesWithProducts.filter(
    (line) =>
      line.product?.[0] &&
      (!line.product[0].translate || line.product[0].translate.length === 0),
  );
  const hasTranslateIssues = linesWithTranslateIssues.length > 0;
  const missingTranslateProductIds = linesWithTranslateIssues
    .map((line) => line.product?.[0]?.productIdx)
    .filter(Boolean);

  return (
    <Modal
      maxWidth={'xl'}
      fullScreenable
      disableContentOverflow
      open={true}
      title={
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <MDTypography variant="h6">
            {`Famous Sales Order #${famousSalesOrder?.famousSalesOrderId}`}
          </MDTypography>
        </MDBox>
      }
      closeable
      onCloseButton={{
        text: 'Close',
        onClick: onClose,
      }}
      hideCloseButton
    >
      <MDBox sx={{ height: '100%', overflow: 'hidden' }}>
        <Grid container spacing={2} sx={{ height: '100%', overflow: 'hidden' }}>
          <Grid
            item
            xs={8}
            sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <MDBox
              sx={{
                width: '100%',
                overflow: 'auto',
                p: 2,
              }}
            >
              {/* Invoice Summary Section */}
              {orderDetails && (
                <MDBox
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    p: 2,
                    mb: 2,
                    backgroundColor: '#fafafa',
                    flexShrink: 0,
                  }}
                >
                  <MDTypography variant="h6" fontWeight="bold" mb={2}>
                    Invoice Summary
                  </MDTypography>

                  <Grid container spacing={2}>
                    {/* Order Information */}
                    <Grid item xs={6}>
                      <MDBox mb={2}>
                        <MDTypography
                          variant="subtitle2"
                          fontWeight="bold"
                          color="primary"
                          mb={1}
                        >
                          Order Information
                        </MDTypography>
                        <MDBox sx={{ fontSize: '12px', lineHeight: 1.5 }}>
                          {orderDetails.orderNumber && (
                            <MDBox mb={0.5}>
                              <MDTypography
                                component="span"
                                variant="caption"
                                fontWeight="bold"
                              >
                                Order #:
                              </MDTypography>
                              <MDTypography
                                component="span"
                                variant="caption"
                                ml={0.5}
                              >
                                {orderDetails.orderNumber}
                              </MDTypography>
                            </MDBox>
                          )}
                          {orderDetails.purchaseOrderNumber && (
                            <MDBox mb={0.5}>
                              <MDTypography
                                component="span"
                                variant="caption"
                                fontWeight="bold"
                              >
                                PO #:
                              </MDTypography>
                              <MDTypography
                                component="span"
                                variant="caption"
                                ml={0.5}
                              >
                                {orderDetails.purchaseOrderNumber}
                              </MDTypography>
                            </MDBox>
                          )}
                          {orderDetails.invoiceDate && (
                            <MDBox mb={0.5}>
                              <MDTypography
                                component="span"
                                variant="caption"
                                fontWeight="bold"
                              >
                                Invoice Date:
                              </MDTypography>
                              <MDTypography
                                component="span"
                                variant="caption"
                                ml={0.5}
                              >
                                {new Date(
                                  orderDetails.invoiceDate,
                                ).toLocaleDateString()}
                              </MDTypography>
                            </MDBox>
                          )}
                          {orderDetails.orderDate && (
                            <MDBox mb={0.5}>
                              <MDTypography
                                component="span"
                                variant="caption"
                                fontWeight="bold"
                              >
                                Order Date:
                              </MDTypography>
                              <MDTypography
                                component="span"
                                variant="caption"
                                ml={0.5}
                              >
                                {new Date(
                                  orderDetails.orderDate,
                                ).toLocaleDateString()}
                              </MDTypography>
                            </MDBox>
                          )}
                          {orderDetails.etaDate && (
                            <MDBox mb={0.5}>
                              <MDTypography
                                component="span"
                                variant="caption"
                                fontWeight="bold"
                              >
                                ETA:
                              </MDTypography>
                              <MDTypography
                                component="span"
                                variant="caption"
                                ml={0.5}
                              >
                                {new Date(
                                  orderDetails.etaDate,
                                ).toLocaleDateString()}
                              </MDTypography>
                            </MDBox>
                          )}
                        </MDBox>
                      </MDBox>

                      {/* Order Totals */}
                      <MDBox mb={2}>
                        <MDTypography
                          variant="subtitle2"
                          fontWeight="bold"
                          color="primary"
                          mb={1}
                        >
                          Order Total:{' '}
                          {orderDetails.amount?.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                          })}
                        </MDTypography>
                      </MDBox>
                    </Grid>

                    {/* Customer & Shipping Information */}
                    <Grid item xs={6}>
                      {/* Customer */}
                      {customer && (
                        <MDBox mb={2}>
                          <MDTypography
                            variant="subtitle2"
                            fontWeight="bold"
                            color="primary"
                            mb={1}
                          >
                            Customer
                          </MDTypography>
                          <MDBox sx={{ fontSize: '12px', lineHeight: 1.5 }}>
                            {/* {customer.description && (
                              <MDBox mb={0.5}>
                                <MDTypography
                                  variant="caption"
                                  fontWeight="bold"
                                >
                                  {customer.description}
                                </MDTypography>
                              </MDBox>
                            )} */}
                            {customer.mailAddress && (
                              <MDBox mb={0.5}>
                                <MDTypography variant="caption">
                                  {customer.mailAddress}
                                </MDTypography>
                              </MDBox>
                            )}
                            {(customer.city ||
                              customer.state ||
                              customer.zip) && (
                              <MDBox mb={0.5}>
                                <MDTypography variant="caption">
                                  {[customer.city, customer.state, customer.zip]
                                    .filter(Boolean)
                                    .join(', ')}
                                </MDTypography>
                              </MDBox>
                            )}
                          </MDBox>
                        </MDBox>
                      )}

                      {/* Ship To */}
                      {shipTo && (
                        <MDBox mb={2}>
                          <MDTypography
                            variant="subtitle2"
                            fontWeight="bold"
                            color="primary"
                            mb={1}
                          >
                            Ship To
                          </MDTypography>
                          <MDBox sx={{ fontSize: '12px', lineHeight: 1.5 }}>
                            {shipTo.name && (
                              <MDBox mb={0.5}>
                                <MDTypography
                                  variant="caption"
                                  fontWeight="bold"
                                >
                                  {shipTo.name}
                                </MDTypography>
                              </MDBox>
                            )}
                            {shipTo.street && (
                              <MDBox mb={0.5}>
                                <MDTypography variant="caption">
                                  {shipTo.street}
                                </MDTypography>
                              </MDBox>
                            )}
                            {(shipTo.city || shipTo.state || shipTo.zip) && (
                              <MDBox mb={0.5}>
                                <MDTypography variant="caption">
                                  {[shipTo.city, shipTo.state, shipTo.zip]
                                    .filter(Boolean)
                                    .join(', ')}
                                </MDTypography>
                              </MDBox>
                            )}
                            {shipTo.DUNS && (
                              <MDBox mb={0.5}>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  fontWeight="bold"
                                >
                                  DUNS:
                                </MDTypography>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  ml={0.5}
                                >
                                  {shipTo.DUNS}
                                </MDTypography>
                              </MDBox>
                            )}
                          </MDBox>
                        </MDBox>
                      )}

                      {/* Payment Terms & Carrier */}
                      <MDBox sx={{ fontSize: '12px', lineHeight: 1.5 }}>
                        {payTerms && (
                          <MDBox mb={1}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              Payment Terms:
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {payTerms.name} ({payTerms.days} days)
                            </MDTypography>
                          </MDBox>
                        )}
                        {carrier && (
                          <MDBox mb={1}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              Carrier:
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {carrier.name || carrier.SCAC}
                            </MDTypography>
                          </MDBox>
                        )}
                        {/* {orderDetails.orderStatus && (
                          <MDBox mb={1}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              Status:
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {orderDetails.orderStatus}
                            </MDTypography>
                          </MDBox>
                        )} */}
                      </MDBox>
                    </Grid>
                  </Grid>

                  {/* Line Items Summary */}
                  {orderDetails.lines &&
                    orderDetails.lines.length > 0 &&
                    linesWithProducts.length > 0 && (
                      <MDBox
                        mt={2}
                        pt={2}
                        sx={{ borderTop: '1px solid #e0e0e0' }}
                      >
                        <MDTypography
                          variant="subtitle2"
                          fontWeight="bold"
                          color="primary"
                          mb={1}
                        >
                          Line Items ({linesWithProducts.length} items with
                          products)
                        </MDTypography>

                        {/* Error alert for products without translate objects */}
                        {hasTranslateIssues && (
                          <MDBox
                            sx={{
                              p: 1,
                              mb: 2,
                              backgroundColor: '#fff3cd',
                              border: '1px solid #ffeaa7',
                              borderRadius: '4px',
                            }}
                          >
                            <MDTypography
                              variant="caption"
                              color="warning.main"
                              fontWeight="bold"
                              sx={{ fontSize: '11px' }}
                            >
                              ⚠️ Tech Alert: {linesWithTranslateIssues.length}{' '}
                              product(s) missing translate data
                            </MDTypography>
                          </MDBox>
                        )}

                        <MDBox>
                          {linesWithProducts.map((line, index) => (
                            <MDBox key={index} mb={1} sx={{ fontSize: '11px' }}>
                              <MDBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems="flex-start"
                              >
                                <MDBox flex={1} mr={2}>
                                  <MDTypography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{ fontSize: '11px' }}
                                  >
                                    {line.name ||
                                      line.product?.[0]?.name ||
                                      `Line ${line.number || index + 1}`}
                                  </MDTypography>
                                  {line.product?.[0]?.variety && (
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        display: 'block',
                                        fontSize: '10px',
                                      }}
                                    >
                                      {line.product[0].variety}{' '}
                                      {line.product[0].grade &&
                                        `- ${line.product[0].grade}`}
                                    </MDTypography>
                                  )}
                                  {/* Show error indicator for this specific line if translate is missing */}
                                  {line.product?.[0] &&
                                    (!line.product[0].translate ||
                                      line.product[0].translate.length ===
                                        0) && (
                                      <MDTypography
                                        variant="caption"
                                        color="error"
                                        sx={{
                                          display: 'block',
                                          fontSize: '10px',
                                          fontStyle: 'italic',
                                        }}
                                      >
                                        ⚠️ Missing translate data
                                      </MDTypography>
                                    )}
                                </MDBox>
                                <MDBox
                                  textAlign="right"
                                  sx={{ minWidth: '120px' }}
                                >
                                  <MDTypography
                                    variant="caption"
                                    sx={{ fontSize: '11px' }}
                                  >
                                    {line.quantity?.toLocaleString()}{' '}
                                    {line.unitOfMeasure || 'units'}
                                  </MDTypography>
                                  {line.price && (
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        display: 'block',
                                        fontSize: '10px',
                                      }}
                                    >
                                      @ ${line.price.toFixed(2)} each
                                    </MDTypography>
                                  )}
                                  {line.saleAmount && (
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      color="success.main"
                                      sx={{
                                        display: 'block',
                                        fontSize: '11px',
                                      }}
                                    >
                                      ${line.saleAmount.toLocaleString()}
                                    </MDTypography>
                                  )}
                                </MDBox>
                              </MDBox>
                            </MDBox>
                          ))}
                        </MDBox>
                      </MDBox>
                    )}
                </MDBox>
              )}

              {/* Raw Data Accordion */}
              <MDBox
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <MDBox
                  onClick={() => setShowRawData(!showRawData)}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    borderRadius: '4px',
                    flexShrink: 0,
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                  }}
                >
                  <Icon sx={{ fontSize: 18 }}>
                    {showRawData ? 'expand_less' : 'expand_more'}
                  </Icon>
                  <MDTypography variant="caption" color="text.secondary">
                    Show Raw Sales Order Data (Technical)
                  </MDTypography>
                </MDBox>

                {showRawData && (
                  <MDBox
                    sx={{
                      display: 'block',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      p: 1,
                      backgroundColor: '#fafafa',
                      flex: 1,
                      overflow: 'auto',
                      minHeight: 0,
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      lineHeight: 1.4,
                    }}
                  >
                    <JsonViewer
                      data={famousSalesOrder as unknown as JsonValue}
                    />
                  </MDBox>
                )}
              </MDBox>
            </MDBox>
          </Grid>
          <Grid
            item
            xs={4}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <MDBox sx={{ p: 2 }}>
              <MDTypography variant="subtitle2" fontWeight="bold" mb={2}>
                EDI {ediInvoiceType} Status
              </MDTypography>
              <SendToEdiButton
                famousSalesOrder={famousSalesOrder}
                sendEdiTransaction={sendEdiTransaction}
                onSendEdi={handleSendEdi}
                hasTranslateIssues={hasTranslateIssues}
                missingTranslateProductIds={missingTranslateProductIds}
                isAdminUser={isAdminUser}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    </Modal>
  );
};

interface SendToEdiButtonProps {
  famousSalesOrder: FamousSalesOrderDTO;
  sendEdiTransaction: ReturnType<typeof useSendEdiTransaction>;
  onSendEdi: (test?: boolean) => void;
  hasTranslateIssues: boolean;
  missingTranslateProductIds: (number | undefined)[];
  isAdminUser: boolean;
}

function SendToEdiButton({
  famousSalesOrder,
  sendEdiTransaction,
  onSendEdi,
  hasTranslateIssues,
  missingTranslateProductIds,
  isAdminUser,
}: SendToEdiButtonProps) {
  const ediInvoiceType = famousSalesOrder.ediInvoiceType;
  const isSent =
    famousSalesOrder.ediInvoiceSentAt && famousSalesOrder.ediInvoiceStatus;
  const sentAt = famousSalesOrder.ediInvoiceSentAt;
  const transactionId = famousSalesOrder.ediInvoiceStatus;
  const isDevelopment = config.isDev;

  function renderSentStatusUi() {
    return (
      <MDBox>
        <MDBox
          sx={{
            p: 2,
            backgroundColor: '#e8f5e8',
            borderRadius: 1,
            border: '1px solid #c8e6c9',
            mb: 2,
          }}
        >
          <MDTypography
            variant="body2"
            fontWeight="bold"
            color="success.main"
            mb={1}
          >
            ✓ EDI {ediInvoiceType} Sent
          </MDTypography>
          <MDTypography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            Sent: {sentAt ? new Date(sentAt).toLocaleDateString() : ''} at{' '}
            {sentAt
              ? new Date(sentAt).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : ''}
          </MDTypography>
          <MDTypography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            Transaction ID: {transactionId}
          </MDTypography>
        </MDBox>

        <MDButton
          variant="outlined"
          color="primary"
          onClick={() => {
            window.open(
              `https://ui.orderful.com/transactions/${transactionId}`,
              '_blank',
            );
          }}
          fullWidth
          sx={{ mb: 2 }}
        >
          View in Orderful
        </MDButton>
      </MDBox>
    );
  }

  function renderSendButtonUi() {
    return (
      <MDBox>
        <MDButton
          variant="contained"
          color="primary"
          onClick={() => onSendEdi(false)}
          disabled={sendEdiTransaction.isLoading || hasTranslateIssues}
          fullWidth
          sx={{ mb: 2 }}
        >
          {sendEdiTransaction.isLoading
            ? 'Sending...'
            : hasTranslateIssues
              ? 'Cannot Send - Missing Translate Data'
              : `Send EDI ${ediInvoiceType} Transaction`}
        </MDButton>

        {isAdminUser && (
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => onSendEdi(true)}
            disabled={sendEdiTransaction.isLoading || hasTranslateIssues}
            fullWidth
            sx={{ mb: 2 }}
          >
            {sendEdiTransaction.isLoading
              ? 'Sending Test...'
              : 'Send Test Transaction'}
          </MDButton>
        )}

        {hasTranslateIssues && (
          <MDBox
            sx={{
              p: 2,
              backgroundColor: '#fff3cd',
              borderRadius: 1,
              border: '1px solid #ffeaa7',
              mb: 2,
            }}
          >
            <MDTypography
              variant="caption"
              color="warning.main"
              fontWeight="bold"
            >
              ⚠️ Cannot Send EDI: Missing translate data
            </MDTypography>
            <MDTypography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1 }}
            >
              Product IDs missing translate data:{' '}
              {missingTranslateProductIds.filter(Boolean).join(', ')}
            </MDTypography>
          </MDBox>
        )}

        {sendEdiTransaction.error && (
          <MDBox
            sx={{
              p: 2,
              backgroundColor: '#ffebee',
              borderRadius: 1,
              border: '1px solid #ffcdd2',
            }}
          >
            <MDTypography variant="caption" color="error">
              Error:{' '}
              {sendEdiTransaction.error?.message ||
                'Failed to send EDI transaction'}
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
    );
  }

  function renderDevelopmentUi() {
    if (isSent) {
      return (
        <MDBox>
          {renderSentStatusUi()}
          <Alert className="mb-2" severity="error">
            This will resend transaction to EDI. Be careful.
          </Alert>
          {renderSendButtonUi()}
        </MDBox>
      );
    }
    return renderSendButtonUi();
  }

  function renderProductionUi() {
    if (isSent) {
      return renderSentStatusUi();
    }
    return renderSendButtonUi();
  }

  return isDevelopment ? renderDevelopmentUi() : renderProductionUi();
}

export default FamousSalesOrderModal;
